import moment from "moment";
import { useRef, useState } from "react";
import { FaxLetterCategory } from "../../../../api/postCall/faxLetter";
import { DocStatusCellData } from "../../../../data-model/faxDocStatus";
import { getCategoryTag } from "../../../../utils/faxLetterCategoryTags";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";
import BadgeCell from "./BadgeCell";

const RETRIEVAL_TAGS: Record<
  string,
  { backgroundColor: string; textColor: string }
> = {
  fax_fetched: {
    backgroundColor: "#EAF2FF",
    textColor: "#0056D6",
  },
  fax_not_checked_yet: {
    backgroundColor: "#F5F5F5",
    textColor: "#3D3D3D",
  },
  checking_fax_inbox: {
    backgroundColor: "#EAF2FF",
    textColor: "#0056D6",
  },
  checked_no_fax_yet: {
    backgroundColor: "#F5F5F5",
    textColor: "#3D3D3D",
  },
  fax_check_failed: {
    backgroundColor: "#FFE8E8",
    textColor: "#CC0300",
  },
};

/** Display real timestamps in the viewer's local timezone, matching the requested examples. */
export function formatDocStatusTime(value?: string | null): string {
  if (!value) return "—";
  const date = moment(value, moment.ISO_8601, true);
  return date.isValid() ? date.local().format("MMM D h:mm A") : "—";
}

/** A Figma status tag with timing available on hover, keyboard focus, or click. */
export default function DocStatusCell({ value }: { value: DocStatusCellData }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  if (!value) return <span>—</span>;
  const tag =
    RETRIEVAL_TAGS[value.status] ??
    getCategoryTag(value.status as FaxLetterCategory);
  return (
    <div
      ref={anchor}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        if (!anchor.current?.contains(document.activeElement)) setOpen(false);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className="flex w-fit max-w-full"
    >
      <button
        type="button"
        aria-label={`${value.text} — fax timing`}
        className="max-w-full rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primaryGray-6"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <BadgeCell
          value={{
            text: value.text,
            color: tag.textColor,
            bgColor: tag.backgroundColor,
          }}
        />
      </button>
      {open && (
        <ToolTipUsingPortal
          parentRef={anchor}
          showTooltip
          placement="top-right"
          maxWidth="320px"
        >
          <div className="grid grid-cols-[auto_auto] gap-x-5 gap-y-2 text-tiny font-normal text-primaryGray-1">
            {value.timestamps.map(({ label, value: timestamp }) => (
              <div key={label} className="contents">
                <span>{label}</span>
                <span>{formatDocStatusTime(timestamp)}</span>
              </div>
            ))}
          </div>
        </ToolTipUsingPortal>
      )}
    </div>
  );
}
